import { Maybe } from '@graphql-tools/utils'
import { DateTime } from 'luxon'
import { paths } from '@/constants'
import { CatalogTitle, findTitleProjectSlug } from '@/services/ContentCatalog'
import { TheatricalLandingReleaseOld } from '@/views/TicketsLandingView/queries'
import { TicketsLandingRelease } from '@/views/TicketsLandingView/types'

export function mapToTicketsLandingRelease(
  theatricalRelease: TheatricalLandingReleaseOld,
): TicketsLandingRelease | null {
  if (!theatricalRelease?.filmDetails) return null

  const releaseDate = mapTheatricalReleaseDate({
    theatricalReleaseDate: theatricalRelease?.filmDetails?.theatricalReleaseDate || '',
    dateDisplayOption: theatricalRelease.dateDisplayOption || '',
  })

  const defaultYear = theatricalRelease?.filmDetails?.releaseYear || ''
  const { releaseYear = defaultYear, releaseMonth = '', releaseDay = '' } = releaseDate ?? {}

  return {
    id: theatricalRelease.sys.id,
    caption: theatricalRelease?.filmDetails?.filmName,
    releaseDay,
    releaseMonth,
    releaseYear,
    releaseDate: theatricalRelease?.filmDetails?.theatricalReleaseDate,
    verticalPoster: theatricalRelease?.filmDetails?.moviePoster,
    name: theatricalRelease?.filmDetails?.filmName as string,
    description: theatricalRelease?.filmDetails?.mediumDescription as string,
    releaseStatus: theatricalRelease.releaseStatus as string,
    linkUrl: theatricalRelease.linkUrl,
    dateDisplayOption: theatricalRelease.dateDisplayOption,
  }
}

export function byReleaseDateDesc(a: Maybe<TicketsLandingRelease>, b: Maybe<TicketsLandingRelease>) {
  if (!a?.releaseDate) return 1
  if (!b?.releaseDate) return -1
  return new Date(b?.releaseDate).getTime() - new Date(a?.releaseDate).getTime()
}

export function byReleaseDateAsc(a: Maybe<TicketsLandingRelease>, b: Maybe<TicketsLandingRelease>) {
  if (!a?.releaseDate) return 1
  if (!b?.releaseDate) return -1
  return new Date(a?.releaseDate).getTime() - new Date(b?.releaseDate).getTime()
}

const FULL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const DateDisplayOptions = ['month-and-year', 'to-be-announced', 'full-date']
type DateDisplayOption = typeof DateDisplayOptions[number]

interface MapTheatricalReleaseArgs {
  theatricalReleaseDate?: string
  dateDisplayOption: DateDisplayOption | string
}

function isDateDisplayOption(maybeDateDisplayOption: unknown): maybeDateDisplayOption is DateDisplayOption {
  return typeof maybeDateDisplayOption === 'string' && DateDisplayOptions.includes(maybeDateDisplayOption)
}

export function mapTheatricalReleaseDate({
  theatricalReleaseDate = '',
  dateDisplayOption = 'to-be-announced',
}: MapTheatricalReleaseArgs): { releaseYear: string; releaseMonth: string; releaseDay: string } | null {
  const isFullDate = FULL_DATE_PATTERN.test(theatricalReleaseDate)

  if (dateDisplayOption === 'to-be-announced' || !isDateDisplayOption(dateDisplayOption) || !isFullDate) {
    return null
  }

  let releaseYear = ''
  let releaseMonth = ''
  let releaseDay = ''

  const releaseYearSplit = isFullDate
    ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' })
        .format(DateTime.fromISO(theatricalReleaseDate).toJSDate())
        .replace(',', '')
        .split(' ')
    : null

  if (releaseYearSplit?.length === 3) {
    if (dateDisplayOption === 'month-and-year') {
      releaseYear = releaseYearSplit[2]
      releaseMonth = releaseYearSplit[0]
      return { releaseYear, releaseMonth, releaseDay: '' }
    } else {
      releaseYear = releaseYearSplit[2]
      releaseMonth = releaseYearSplit[0]
      releaseDay = releaseYearSplit[1]
      return { releaseYear, releaseMonth, releaseDay }
    }
  }

  return null
}

export function formatShowtimesUrl(title: CatalogTitle): string {
  return `${paths.tickets.index}/${findTitleProjectSlug(title)}`
}
